import { useEffect, useCallback } from "react";

import { atom, useAtom } from "jotai";
import {
  atomFamily,
  atomWithStorage,
  useAtomCallback,
  useAtomValue,
} from "jotai/utils";
import deepEqual from "fast-deep-equal";

import { DataFile } from "interfaces";
import {
  OutlierTreatment,
  outlierTIsClampChar,
} from "interfaces/data-files/data-file-create";

import { useGetFileColumns } from "./file-api";
import { columnType } from "./column-types";
import { useFetchColumnProfile } from "./column-insights";
import { retainedColumns, columnRetainStatus } from "./keep-drop";

interface WIPNumOutlierTreatment {
  range?: number[];
  floor?: number;
  ceiling?: number;
}

interface WIPDateOutlierTreatment {
  range?: Date[];
  floor?: Date;
  ceiling?: Date;
}

interface WIPCharOutlierTreatment {
  range?: string[];
  replacement?: string;
}

interface Spec {
  fileId: string;
  column: string;
}

const outlierTreatmentOption = atomFamily(
  ({ fileId, column }: Spec) =>
    atomWithStorage<OutlierTreatment["treatment"]>(
      `data-files/outlier-treatment/treatment/${fileId}/${column}`,
      "ignore"
    ),
  deepEqual
);

export const useSetBulkOutlierTreatment = (
  fileId: string,
  option: "ignore" | "drop",
  columns?: string[]
) => {
  const fileColumns = useGetFileColumns(fileId);
  if (!columns) {
    columns = fileColumns;
  }
  return useAtomCallback(
    useCallback(
      (_, set) => {
        columns.forEach((c) =>
          set(outlierTreatmentOption({ fileId, column: c }), option)
        );
      },
      [fileId, option, columns]
    )
  );
};

const numericTreatment = atomFamily(
  ({ fileId, column }: Spec) =>
    atomWithStorage<WIPNumOutlierTreatment>(
      `data-files/outlier-treatment/numeric/${fileId}/${column}`,
      {}
    ),
  deepEqual
);

const charTreatment = atomFamily(
  ({ fileId, column }: Spec) =>
    atomWithStorage<WIPCharOutlierTreatment>(
      `data-files/outlier-treatment/char/${fileId}/${column}`,
      {}
    ),
  deepEqual
);

const dateTreatment = atomFamily(
  ({ fileId, column }: Spec) =>
    atomWithStorage<WIPDateOutlierTreatment>(
      `data-files/outlier-treatment/date/${fileId}/${column}`,
      {}
    ),
  deepEqual
);

export const colOutlierTreatment = atomFamily(
  ({ fileId, column }: Spec) =>
    atom<
      | WIPNumOutlierTreatment
      | WIPCharOutlierTreatment
      | WIPDateOutlierTreatment,
      WIPNumOutlierTreatment | WIPCharOutlierTreatment | WIPDateOutlierTreatment
    >(
      (get) => {
        switch (get(columnType({ fileId, column }))) {
          case "numeric":
            return get(numericTreatment({ fileId, column }));
          case "char":
            return get(charTreatment({ fileId, column }));
          case "date":
            return get(dateTreatment({ fileId, column }));
          default:
            return {};
        }
      },
      (get, set, treatment) => {
        switch (get(columnType({ fileId, column }))) {
          case "numeric":
            const nt = get(numericTreatment({ fileId, column }));
            set(numericTreatment({ fileId, column }), {
              ...nt,
              ...treatment,
            } as WIPNumOutlierTreatment);
          case "char":
            const ct = get(charTreatment({ fileId, column }));
            set(charTreatment({ fileId, column }), {
              ...ct,
              ...treatment,
            } as WIPCharOutlierTreatment);
          case "date":
            const dt = get(dateTreatment({ fileId, column }));
            set(dateTreatment({ fileId, column }), {
              ...dt,
              ...treatment,
            } as WIPDateOutlierTreatment);
        }
      }
    ),
  deepEqual
);

export const columnOutlierTreatmentSpec = atomFamily(
  ({ fileId, column }: Spec) =>
    atom<OutlierTreatment, OutlierTreatment>(
      (get) => {
        const option = get(outlierTreatmentOption({ fileId, column }));
        if (option === "ignore") {
          return { column, treatment: option };
        }
        const treatment = get(colOutlierTreatment({ fileId, column }));
        return { column, treatment: option, ...treatment } as OutlierTreatment;
      },
      (get, set, newValue) => {
        const option = get(outlierTreatmentOption({ fileId, column }));
        const treatment = (newValue as OutlierTreatment).treatment;

        if (treatment !== option) {
          set(outlierTreatmentOption({ fileId, column }), treatment);
          if (treatment === "ignore") {
            return;
          }
        }

        const {
          column: c,
          treatment: t,
          ...rest
        } = newValue as OutlierTreatment;
        set(colOutlierTreatment({ fileId, column }), rest);
      }
    ),
  deepEqual
);

export const fileOutlierTreatmentSpec = atomFamily((fileId: string) =>
  atom<OutlierTreatment[]>((get) => {
    const columns = get(retainedColumns(fileId));
    return columns.map((column) =>
      get(columnOutlierTreatmentSpec({ fileId, column }))
    );
  })
);

interface ValidationError {
  range?: string;
  replacement?: string;
  floor?: string;
  ceiling?: string;
}

export const validateOutlierTreatment = (
  spec: OutlierTreatment,
  type: "numeric" | "char" | "date",
  isDropped?: boolean
): ValidationError | undefined => {
  if (isDropped) {
    return;
  }

  let err: ValidationError = {};
  if (spec.treatment === "clamp" || spec.treatment === "drop") {
    const { range } = spec;
    if (!Array.isArray(range)) {
      err.range = "Outliers Not Specified";
    } else if (type === "char") {
      if (range.length === 0) {
        err.range = "Outliers Not Specified";
      }
    } else if (type === "date") {
      const dateRange = (range as (string | number | Date)[])
        .map((r) => new Date(r))
        .filter((d) => !isNaN(d.valueOf()));
      if (dateRange.length !== 2) {
        err.range = "Invalid outlier definitions specified";
      }
    } else if (type === "numeric") {
      const numRange = range.filter(
        // @ts-ignore
        (d) => d !== undefined && d !== null && d !== "" && !isNaN(d as number)
      );
      if (numRange.length !== 2) {
        err.range = "Invalid outlier definitions specified";
      }
    }
  }

  if (spec.treatment === "clamp") {
    if (outlierTIsClampChar(spec)) {
      if (!spec.replacement) {
        err.replacement = "Replacement value Not Specified";
      }
    } else if (type === "numeric") {
      const { floor, ceiling } = spec;

      if (
        floor === null ||
        floor === undefined ||
        floor?.toString() === "" ||
        isNaN(floor as number)
      ) {
        err.floor = "Floor value is NOT VALID";
      }
      if (ceiling?.toString() === "" || isNaN(ceiling as number)) {
        err.ceiling = "Ceiling value is NOT VALID";
      }
    } else if (type === "date") {
      const { floor, ceiling } = spec;
      const floorD = new Date(floor === undefined ? "" : floor);
      const celingD = new Date(ceiling === undefined ? "" : ceiling);
      if (isNaN(floorD.valueOf())) {
        err.floor = "Floor value is NOT VALID";
      }
      if (isNaN(celingD.valueOf())) {
        err.ceiling = "Ceiling value is NOT VALID";
      }
    }
  }

  return Object.keys(err).length === 0 ? undefined : err;
};

export const useValidateColumnOutlierTreatment = (
  fileId: string,
  column: string
) => {
  const spec = useAtomValue(columnOutlierTreatmentSpec({ fileId, column }));
  const colType = useAtomValue(columnType({ fileId, column }));
  const retainStatus = useAtomValue(columnRetainStatus({ fileId, column }));
  return validateOutlierTreatment(spec, colType, retainStatus === "drop");
};

export const getInitOutlierTreatment = (
  colType: "char" | "numeric" | "date",
  stats: DataFile.ColumnStatsInterface
): {
  option: OutlierTreatment["treatment"];
  range: (number | string | Date)[];
} => {
  const range =
    colType === "char"
      ? (stats["outlier-ranges"] || []).map((v) => v.toString())
      : colType === "date"
      ? (stats["outlier-ranges"] || []).map((v) => new Date(v))
      : stats["outlier-ranges"] || [];

  if (stats["unique-count"] <= 1 || range.length === 0) {
    return { option: "ignore", range };
  }

  // CHECK IF OUTLIER-RANGES ARE INSIDE MIN AND MAX.
  if (colType === "numeric") {
    const { min, max } = stats;
    if (min !== undefined && max !== undefined) {
      const range = stats["outlier-ranges"];
      if (range.length === 2) {
        if (min >= range[0] && max <= range[1]) {
          return { option: "ignore", range };
        }
      }
    }
  } else if (colType === "char") {
    const range = stats["outlier-ranges"];
    if (!Array.isArray(range)) {
      return { option: "ignore", range };
    }
    if (range.length === 0) {
      return { option: "ignore", range };
    }
  } else if (colType === "date") {
    const { min, max } = stats;
    if (min !== undefined && max !== undefined) {
      // @ts-ignore
      const range = stats["outlier-ranges"].map((d) => Date.parse(d));
      if (range.length === 2) {
        // @ts-ignore
        if (Date.parse(min) >= range[0] && Date.parse(max) <= range[1]) {
          return { option: "ignore", range };
        }
      }
    }
  }

  return { option: "drop", range };
};

export const useInitOutlierTreatment = (fileId: string, column: string) => {
  const colType = useAtomValue(columnType({ fileId, column }));
  const { data } = useFetchColumnProfile(fileId, column, colType);

  const [option, setOption] = useAtom(
    outlierTreatmentOption({ fileId, column })
  );
  const [currValue, setValues] = useAtom(
    colOutlierTreatment({ fileId, column })
  );

  useEffect(() => {
    if ((option === null || option === undefined) && data) {
      const { option, range } = getInitOutlierTreatment(colType, data.stats);
      setOption(option);
      if (
        range !== null &&
        range !== undefined &&
        currValue !== null &&
        currValue !== undefined
      ) {
        // @ts-ignore
        setValues({ range });
      }
    }
  }, [data, option, currValue, setOption, setValues]);
};
